import {useMemo} from "react";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useDoorStates} from "@/encounter/doors/DoorStateHooks.ts";
import {sightBlockers, visibilityPolygon, visionSources} from "./VisibilityGeometry.ts";
import {DEFAULT_SIGHT_RADIUS_METERS} from "./SightRange.ts";
import {perceptionProfile} from "./PerceptionProfile.ts";
import {imageToWorld, PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";
import type {Visibility} from "./VisibilityModel.ts";
import type {VisibilityPolygon} from "./VisibilityGeometry.ts";
import {effectiveLights, lightAreas} from "./LightGeometry.ts";
import {useEncounterLights} from "@/encounter/lights/EncounterLightHooks.ts";
import {effectiveLevelLighting} from "@/encounter/lights/EncounterLights.ts";

export function useVisibility(side: "friendly" | "hostile", calculate = true): Visibility {
    const {dungeon, dungeonSettings} = useDungeon();
    const {combatants} = useCombatants();
    const {stateFor} = useDoorStates();
    const {state: lightState} = useEncounterLights();
    const radiusMeters = DEFAULT_SIGHT_RADIUS_METERS;
    const level = dungeon.levels.find(candidate => candidate.id === dungeonSettings.levelId);
    const blockers = useMemo(
        () => calculate && level ? sightBlockers(level.geometry, stateFor) : [],
        [calculate, level, stateFor],
    );
    const blockerKey = useMemo(() => JSON.stringify(blockers), [blockers]);
    const lightPolygonCache = useMemo(() => new Map<string, VisibilityPolygon>(), []);
    const sources = useMemo(() => calculate && level
        ? visionSources(combatants, level.id, side) : [], [calculate, combatants, level, side]);
    const areas = useMemo(() => calculate && level
        ? sources.map(source => {
            const perception = perceptionProfile(source.character.senses);
            return {origin: source.position, perception,
                polygon: visibilityPolygon(source.position, blockers, perception.geometryRangeMeters * PIXELS_PER_METER),
                rotationDegrees: source.rotation};
        }).filter(area => area.polygon.length >= 3)
        : [], [blockers, calculate, level, sources]);
    const polygons = useMemo(() => areas.map(area => area.polygon), [areas]);
    const lights = useMemo(() => calculate && level
        ? lightAreas(effectiveLights(level.geometry, level.id, lightState, combatants), blockers,
            lightPolygonCache, blockerKey)
        : [], [blockers, calculate, level, lightState, combatants, lightPolygonCache, blockerKey]);
    const lighting = useMemo(() => {
        if (!level) return undefined;
        const start = imageToWorld({x: 0, y: 0}, level.geometry);
        const end = imageToWorld({x: level.map.image.width, y: level.map.image.height}, level.geometry);
        return {bounds: {minX: start.x, minY: start.y, maxX: end.x, maxY: end.y}, blockers,
            directionalLight: effectiveLevelLighting(level.geometry.directionalLight, level.id, lightState)};
    }, [level, blockers, lightState]);
    return {enabled: Boolean(level), levelId: level?.id, areas, lights, lighting, polygons, blockers, blockerKey, radiusMeters};
}
